// Web Push helpers: service-worker registration + subscription lifecycle.
import * as api from './api';

const SW_URL = '/sw.js';

// Push needs ServiceWorker + PushManager + Notification. On iOS, this is only
// available once the PWA has been added to the Home Screen (installed).
export const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const getPermission = () =>
  (typeof Notification !== 'undefined' ? Notification.permission : 'denied');

// iOS only exposes push inside an installed (standalone) PWA.
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ reports as Mac; detect via touch points
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register(SW_URL);
    return registration;
  } catch (e) {
    console.error('Service worker registration failed:', e);
    return null;
  }
}

// VAPID public key (base64url) -> Uint8Array for applicationServerKey.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

// Request OS permission, subscribe via PushManager, and persist on the backend.
// Returns the PushSubscription. Throws with a coded reason on failure.
export async function enablePush() {
  if (!isPushSupported()) {
    const err = new Error('unsupported');
    err.code = 'unsupported';
    throw err;
  }

  const { public_key: publicKey, enabled } = await api.getVapidPublicKey();
  if (!enabled || !publicKey) {
    const err = new Error('server-not-configured');
    err.code = 'server-not-configured';
    throw err;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const err = new Error('permission-denied');
    err.code = 'permission-denied';
    throw err;
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await api.savePushSubscription(subscription.toJSON());
  setTurnedOff(false);
  return subscription;
}

// Remembers on this device that the user switched notifications off in
// Settings, so syncPushOnLoad never re-subscribes them behind their back.
const OFF_KEY = 'mp_push_off';
function setTurnedOff(off) {
  try {
    if (off) localStorage.setItem(OFF_KEY, '1');
    else localStorage.removeItem(OFF_KEY);
  } catch (_) { /* storage unavailable */ }
}
export function wasTurnedOff() {
  try { return localStorage.getItem(OFF_KEY) === '1'; } catch (_) { return false; }
}

// Called on app load: if the browser still has a live, permitted subscription,
// re-save it server-side. A subscription can silently change (renewal, SW
// re-registration) without the user ever re-toggling notifications — without
// this, the server keeps sending to a dead endpoint forever and the user just
// stops getting notifications with no visible sign anything's wrong.
export async function resyncSubscription() {
  if (!isPushSupported() || getPermission() !== 'granted') return;
  try {
    const subscription = await getCurrentSubscription();
    if (subscription) await api.savePushSubscription(subscription.toJSON());
  } catch (_) { /* best effort */ }
}

// Called on every app load. Brings this device's push back without a prompt
// when it can, and reports the device's real state so /admin doesn't count a
// server-side row that the phone no longer backs:
//  - OS permission still granted but the subscription is gone (iOS drops
//    them, SW updates, a 410 pruned it server-side) → subscribe again
//    silently, save it, and turn the account's notifications back on.
//  - permission granted and subscribed → re-save (endpoints can rotate).
// Returns { supported, permission, subscribed, resubscribed }.
export async function syncPushOnLoad() {
  if (!isPushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const permission = getPermission();
  let subscription = null;
  let resubscribed = false;
  try {
    subscription = await withTimeout(getCurrentSubscription(), 8000);
    if (permission === 'granted' && !subscription && !wasTurnedOff()) {
      const { public_key: publicKey, enabled } = await api.getVapidPublicKey();
      if (enabled && publicKey) {
        const registration = await withTimeout(navigator.serviceWorker.ready, 8000);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        resubscribed = true;
      }
    }
    if (permission === 'granted' && subscription) await api.savePushSubscription(subscription.toJSON());
  } catch (e) {
    console.warn('Push sync on load failed:', e);
  }
  const state = { supported: true, permission, subscribed: !!subscription && permission === 'granted', resubscribed };
  api.reportPushDeviceState({
    permission, subscribed: state.subscribed, standalone: isStandalone(),
    endpoint: subscription?.endpoint || null,
  }).catch(() => {});
  return state;
}

function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))]);
}

// Unsubscribe locally and remove from the backend.
export async function disablePush() {
  setTurnedOff(true);
  const subscription = await getCurrentSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } catch (e) {
    /* ignore — still remove server-side */
  }
  try {
    await api.deletePushSubscription(endpoint);
  } catch (e) {
    /* ignore */
  }
}

// Convert a local "HH:MM" the user picked into the UTC "HH:MM" the backend
// scheduler compares against.
export function localTimeToUtc(localHHMM) {
  const [h, m] = localHHMM.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
